import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BetaUpdatesPanel.css';

const API = process.env.REACT_APP_API_URL;

function BetaUpdatesPanel({ onClose }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'compose'
  const [editingId, setEditingId] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [notes, setNotes] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null); // id being sent
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    loadUpdates();
  }, []);

  const loadUpdates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/beta-updates`, { withCredentials: true });
      setUpdates(res.data.updates);
    } catch (err) {
      setError('Failed to load updates');
    } finally {
      setLoading(false);
    }
  };

  const handleDraftWithAI = async () => {
    setDrafting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/beta-updates/draft`, { notes }, { withCredentials: true });
      setSubject(res.data.subject);
      setBody(res.data.body);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate draft');
    } finally {
      setDrafting(false);
    }
  };

  const handleSave = async () => {
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await axios.put(`${API}/api/beta-updates/${editingId}`, { subject, body }, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/beta-updates`, { subject, body }, { withCredentials: true });
      }
      await loadUpdates();
      resetCompose();
      setView('list');
      flash('Draft saved!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (id) => {
    setSending(id);
    setError('');
    try {
      const res = await axios.post(`${API}/api/beta-updates/${id}/send`, {}, { withCredentials: true });
      await loadUpdates();
      flash(`Sent to ${res.data.recipientCount} tester${res.data.recipientCount === 1 ? '' : 's'}!`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send');
    } finally {
      setSending(null);
    }
  };

  const handleEdit = (update) => {
    setEditingId(update.id);
    setSubject(update.subject);
    setBody(update.body);
    setView('compose');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axios.delete(`${API}/api/beta-updates/${id}`, { withCredentials: true });
      await loadUpdates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const resetCompose = () => {
    setEditingId(null);
    setSubject('');
    setBody('');
    setNotes('');
    setError('');
  };

  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const drafts = updates.filter(u => !u.sent_at);
  const sent = updates.filter(u => u.sent_at);

  return (
    <div className="bup-overlay" onClick={onClose}>
      <div className="bup-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="bup-header">
          <div className="bup-header-left">
            {view === 'compose' && (
              <button className="bup-back-btn" onClick={() => { setView('list'); resetCompose(); }}>
                ← Back
              </button>
            )}
            <div>
              <h2>{view === 'compose' ? (editingId ? 'Edit Draft' : 'New Update') : 'Beta Updates'}</h2>
              <p>{view === 'compose' ? 'Write or AI-draft an update for your testers' : 'Compose and send updates to your beta testers'}</p>
            </div>
          </div>
          <button className="bup-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Alerts */}
        {error && <div className="bup-alert bup-alert-error">{error}</div>}
        {successMsg && <div className="bup-alert bup-alert-success">✓ {successMsg}</div>}

        {/* LIST VIEW */}
        {view === 'list' && (
          <div className="bup-body">
            <div className="bup-list-header">
              <button className="bup-new-btn" onClick={() => { resetCompose(); setView('compose'); }}>
                + New Update
              </button>
            </div>

            {loading ? (
              <div className="bup-loading">Loading updates...</div>
            ) : updates.length === 0 ? (
              <div className="bup-empty">
                <div className="bup-empty-icon">✉</div>
                <h3>No updates yet</h3>
                <p>Click "New Update" to write your first beta tester brief.</p>
              </div>
            ) : (
              <>
                {drafts.length > 0 && (
                  <div className="bup-section">
                    <div className="bup-section-label">Drafts</div>
                    {drafts.map(u => (
                      <div key={u.id} className="bup-update-card bup-draft">
                        <div className="bup-update-info">
                          <span className="bup-status-badge bup-badge-draft">Draft</span>
                          <h4>{u.subject}</h4>
                          <span className="bup-update-date">Created {new Date(u.created_at).toLocaleDateString('en-CA')}</span>
                        </div>
                        <div className="bup-update-actions">
                          <button className="bup-btn-ghost" onClick={() => handleEdit(u)}>Edit</button>
                          <button
                            className="bup-btn-send"
                            onClick={() => handleSend(u.id)}
                            disabled={sending === u.id}
                          >
                            {sending === u.id ? 'Sending...' : '✉ Send to Testers'}
                          </button>
                          <button className="bup-btn-delete" onClick={() => handleDelete(u.id)}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {sent.length > 0 && (
                  <div className="bup-section">
                    <div className="bup-section-label">Sent</div>
                    {sent.map(u => (
                      <div key={u.id} className="bup-update-card bup-sent">
                        <div className="bup-update-info">
                          <span className="bup-status-badge bup-badge-sent">Sent</span>
                          <h4>{u.subject}</h4>
                          <span className="bup-update-date">Sent {new Date(u.sent_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* COMPOSE VIEW */}
        {view === 'compose' && (
          <div className="bup-body">
            <div className="bup-ai-bar">
              <div className="bup-ai-bar-top">
                <div className="bup-ai-bar-left">
                  <span className="bup-ai-icon">✦</span>
                  <div>
                    <strong>Draft with AI</strong>
                    <span>Jot down what changed — Claude writes the polished email.</span>
                  </div>
                </div>
                <button
                  className="bup-ai-btn"
                  onClick={handleDraftWithAI}
                  disabled={drafting}
                >
                  {drafting ? 'Drafting...' : 'Generate Draft'}
                </button>
              </div>
              <textarea
                className="bup-notes-textarea"
                placeholder={"e.g.\n- Added PDF report builder in Settings\n- New in-app feedback box on the dashboard\n- Fixed social URL fields not saving on new clients"}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={4}
              />
            </div>

            <div className="bup-compose-form">
              <div className="bup-field">
                <label>Subject Line</label>
                <input
                  type="text"
                  className="bup-input"
                  placeholder="e.g. New in ReportFlow: PDF Builder + Feedback Tool"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                />
              </div>
              <div className="bup-field">
                <label>Email Body <span className="bup-label-hint">(HTML supported)</span></label>
                <textarea
                  className="bup-textarea"
                  placeholder="Write your update here, or use Generate Draft above..."
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={14}
                />
              </div>

              {body && (
                <div className="bup-preview-section">
                  <div className="bup-preview-label">Preview</div>
                  <div
                    className="bup-preview-body"
                    dangerouslySetInnerHTML={{ __html: body }}
                  />
                </div>
              )}

              <div className="bup-compose-actions">
                <button className="bup-btn-cancel" onClick={() => { setView('list'); resetCompose(); }}>
                  Cancel
                </button>
                <button className="bup-btn-save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default BetaUpdatesPanel;
